/**
 * Week section — a single disease heading inside the WeekView. Composes
 * three `weekSubsection` blocks (临床研究 / 人工智能… / 其他基础研究)
 * in the fixed order prescribed by convention #11.
 *
 * Header rules:
 *
 *   ┌─ short disease label (e.g. "气胸")  ⌐── Convention #4        ┐
 *   │   full subtitle ("气胸、胸部外伤、…")   ⌐── rendered small     │
 *   ├─ 3 type subsections (临床 / AI / 其他基础研究)                │
 *
 * The short label uses `diseaseShortLabel()`; the subtitle uses the
 * full string verbatim — collapsing it is forbidden by convention #4.
 */

import { el } from "../dom";
import { weekSubsection } from "./weekSubsection";
import type {
  DiseaseKey,
  TypeLabel,
  WeekIncludedItem,
  WeekSection,
} from "../types";

export interface WeekSectionOptions {
  disease_key: DiseaseKey;
  /** Subtitle shown under the heading — for the 4th disease this is the
   * full string `气胸、胸部外伤、肋骨骨折、胸壁畸形`. */
  disease_subtitle: string;
  /** Pre-grouped items, keyed by type. */
  items_by_type: Record<TypeLabel, WeekIncludedItem[]>;
  /** Raw section metadata for the 3 subsections. */
  sections: WeekSection[];
}

const TYPE_ORDER: readonly TypeLabel[] = [
  "临床研究",
  "人工智能/机器学习相关研究",
  "其他基础研究",
];

export function weekSection({
  disease_key,
  disease_subtitle,
  items_by_type,
  sections,
}: WeekSectionOptions): HTMLElement {
  const root = el("section", {
    class: "week-section",
    "data-disease": disease_key,
  }, [
    el("div", { class: "week-section-head" }, [
      el("h3", { class: "week-section-title" }, diseaseShortLabel(disease_key)),
      el("p", { class: "week-section-subtitle" }, disease_subtitle),
    ]),
  ]);

  for (const type of TYPE_ORDER) {
    const sub = sections.find((s) => s.disease_key === disease_key && s.type_label === type);
    if (!sub) {
      // Worker always emits a row per type even when empty; missing
      // means the row hasn't been generated yet — render an empty
      // stub instead of skipping.
      root.appendChild(
        weekSubsection({
          section: {
            disease_label: diseaseShortLabel(disease_key),
            disease_key,
            type_label: type,
            paragraphs: [],
            empty_note: `本周该疾病无 ${type} 文献纳入`,
          },
          items: items_by_type[type] ?? [],
        }),
      );
      continue;
    }
    root.appendChild(
      weekSubsection({
        section: sub,
        items: items_by_type[type] ?? [],
      }),
    );
  }

  return root;
}

export function diseaseShortLabel(key: DiseaseKey): string {
  switch (key) {
    case "lung_cancer":
      return "肺癌";
    case "mediastinal_tumor":
      return "纵隔肿瘤";
    case "esophageal_cancer":
      return "食管癌";
    case "pneumothorax":
      return "气胸";
    case "mediastinal_tumor_supplement":
      return "纵隔肿瘤（补充）";
    default:
      return key;
  }
}

export const DISEASE_ORDER: readonly DiseaseKey[] = [
  "lung_cancer",
  "mediastinal_tumor",
  "esophageal_cancer",
  "pneumothorax",
];

/** Full subtitle — convention #4 demands the 4th disease's subtitle
 * match this exact string. */
export const PNEUMOTHORAX_SUBTITLE = "气胸、胸部外伤、肋骨骨折、胸壁畸形";